import 'react';
import {Box, Table, TableBody, TableCell, TableRow} from '@mui/material';
import type {ProfitsTaxBVO} from '@/services/auditService/interface/tax-com';
import {MoneyField} from '@/components/common';
import {amountCellSx, descCellSx, labelCell, labelCellSx} from '@/pages/components/tax-computation/style/tax-style';
import {TaxMainP1Footnotes} from '@/pages/components/tax-computation/main/TaxMainP1Footnotes';

interface TaxMainTypeBProps {
    data?: ProfitsTaxBVO | null;
    currency?: string | null;
    taxPayment?: number;
    taxPaymentChange?: (taxPayment: number) => void;
    taxLossBf?: number;
    taxLossBfChange?: (taxLossBf: number) => void;
    note?: string | null;
    confirm?: string | null;
    noteChange?: (note: string) => void;
    confirmChange?: (confirm: string) => void;
    onTextBlur?: () => void;
}

export function TaxMainTypeB({
                                 data,
                                 currency,
                                 taxPayment,
                                 taxPaymentChange,
                                 taxLossBf,
                                 taxLossBfChange,
                                 note,
                                 confirm,
                                 noteChange,
                                 confirmChange,
                                 onTextBlur,
                             }: TaxMainTypeBProps) {
    const renderAmount = (value?: number | null) => {
        if (value === undefined || value === null) return null;
        return (
            <MoneyField
                value={value}
                editable={false}
                currencySymbol={currency || ''}
                showCents={false}
                align="right"
            />
        );
    };

    const shouldHideRow = (value?: number | null) => value === 0;
    const paymentValue = taxPayment ?? data?.provisionalTaxPayment ?? 0;
    const paymentEditable = !!taxPaymentChange;
    // Always show as deduction (negative / parentheses), same as Excel export
    const paymentDisplayValue = paymentValue === 0 ? 0 : -Math.abs(paymentValue);
    const taxLossBfValue = taxLossBf ?? data?.taxLossBf ?? 0;
    const showAdjustedLoss = (data?.adjustedAssessment ?? 0) !== 0;
    const showTaxLossCf = (data?.taxLossCf ?? 0) !== 0;

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
            <Table size="small">
            <TableBody>
                <TableRow>
                    <TableCell colSpan={3} sx={{py: 0.25, verticalAlign: 'middle'}}/>
                </TableRow>

                {shouldHideRow(data?.donationMadeValue) ? null : (
                    <TableRow>
                        <TableCell sx={labelCell}>LESS:</TableCell>
                        <TableCell sx={descCellSx}>
                            Donations made to approved charitable organizations<br/>
                            (restricted to 35% of Y)
                        </TableCell>
                        <TableCell sx={amountCellSx} align="right">{renderAmount(data?.donationMadeValue)}</TableCell>
                    </TableRow>
                )}

                <TableRow>
                    <TableCell colSpan={3} sx={descCellSx}/>
                </TableRow>

                <TableRow>
                    <TableCell
                        sx={labelCell}>{(data?.assessmentValue ?? 0) < 0 ? 'ADJUSTED LOSS:' : 'ASSESSABLE PROFIT:'}</TableCell>
                    <TableCell sx={descCellSx}>{data?.assessmentName || ''}</TableCell>
                    <TableCell sx={amountCellSx} align="right">{renderAmount(data?.assessmentValue)}</TableCell>
                </TableRow>

                <TableRow>
                    <TableCell colSpan={3} sx={descCellSx}/>
                </TableRow>

                <TableRow>
                    <TableCell colSpan={3} sx={labelCell} align='center'>STATEMENT OF LOSS</TableCell>
                </TableRow>

                <TableRow>
                    <TableCell colSpan={3} sx={descCellSx}/>
                </TableRow>

                <TableRow>
                    <TableCell sx={labelCellSx}>Final assessment:-</TableCell>
                    <TableCell colSpan={2} sx={descCellSx}/>
                </TableRow>

                <TableRow>
                    <TableCell colSpan={2} sx={labelCellSx}>Tax loss b/f</TableCell>
                    <TableCell sx={{...amountCellSx, paddingRight: 0.5}} align="right">
                        <MoneyField
                            value={taxLossBfValue}
                            editable={!!taxLossBfChange}
                            tiny
                            currencySymbol={currency || ''}
                            showCents={false}
                            align="right"
                            onChangeNumber={(n) => {
                                const next = n > 0 ? -Math.abs(n) : n;
                                taxLossBfChange?.(next);
                            }}
                        />
                    </TableCell>
                </TableRow>

                {showAdjustedLoss ? (
                    <TableRow>
                        <TableCell colSpan={2} sx={labelCellSx}>{data?.adjustedAssessmentName || ''}</TableCell>
                        <TableCell sx={amountCellSx} align="right">{renderAmount(data?.adjustedAssessment)}</TableCell>
                    </TableRow>
                ) : null}

                {showTaxLossCf ? (
                    <TableRow>
                        <TableCell colSpan={2} sx={labelCellSx}>Tax loss c/f</TableCell>
                        <TableCell sx={amountCellSx} align="right">{renderAmount(data?.taxLossCf)}</TableCell>
                    </TableRow>
                ) : null}

                <TableRow>
                    <TableCell colSpan={3} sx={descCellSx}/>
                </TableRow>

                <TableRow>
                    <TableCell colSpan={2} sx={{...labelCellSx, py: 0}}>Provisional tax payment paid
                        previously</TableCell>
                    <TableCell sx={{...amountCellSx, py: 0, paddingRight: 0.5}} align="right">
                        <MoneyField
                            value={paymentDisplayValue}
                            editable={paymentEditable}
                            tiny
                            currencySymbol={currency || ''}
                            showCents={false}
                            align="right"
                            onChangeNumber={(n) => taxPaymentChange?.(Math.abs(n))}
                        />
                    </TableCell>
                </TableRow>
            </TableBody>
            </Table>

            <TaxMainP1Footnotes
                note={note}
                confirm={confirm}
                fallbackNote={data?.note}
                fallbackConfirm={data?.confirm}
                defaultNote=""
                noteChange={noteChange}
                confirmChange={confirmChange}
                onTextBlur={onTextBlur}
            />
        </Box>
    );
}
